import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, tap, timeout } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SharedDataService {
  private apiService = inject(ApiService);

  /** How long to wait for the config API before giving up and keeping the default. */
  private static readonly PORTAL_URL_TIMEOUT_MS = 8000;

  /**
   * Emits a portal URL that is GUARANTEED to never be undefined/empty.
   *
   * - Warm cache: emits the cached value immediately (synchronously), no API call.
   * - Cold cache: emits `environment.portalUrl` IMMEDIATELY as the default, then
   *   upgrades to the server value once it arrives — but only if the server
   *   actually returns a proper (non-empty) URL. If the server is missing,
   *   hanging (timeout), erroring, or returns an empty value, the environment
   *   default is kept. Only genuine server values are cached, so a temporary
   *   outage never permanently pins us to the fallback.
   */
  getPortalUrl(): Observable<string> {
    const cached = this.getData<string>('portalURL');
    if (typeof cached === 'string' && cached.trim()) {
      return of(cached.trim());
    }
    return this.apiService.GetConfigurationParam('PortalURL').pipe(
      timeout({ first: SharedDataService.PORTAL_URL_TIMEOUT_MS }),
      // Normalize: a proper server URL stays, anything falsy/blank becomes null.
      map(url => (typeof url === 'string' && url.trim() ? url.trim() : null)),
      // Cache ONLY a genuine server value — never the fallback.
      tap(url => { if (url) { this.setData<string>('portalURL', url); } }),
      // Improper server response -> fall back to the build-time constant.
      map(url => url ?? environment.portalUrl),
      // Emit the default first so subscribers are never left with undefined,
      // even before the server responds or if it never does.
      startWith(environment.portalUrl),
      // Avoid a redundant second emission when the server value equals the default.
      distinctUntilChanged(),
      // Timeout / network / server error: the default was already emitted; keep it.
      catchError(() => of(environment.portalUrl))
    );
  }

  setData<T>(key: string, data: T): void {
    // Save data to the class variable as well as localStorage
    localStorage.setItem(key, JSON.stringify(data));
  }

  getData<T>(key: string): T | null {
    const storedData = localStorage.getItem(key);

    if (storedData) {
      try {
        // Parse stored data from string to its original form
        return JSON.parse(storedData) as T;
      } catch (error) {
        console.error('Error parsing stored data from localStorage', error);
        return null;
      }
    }

    return null;
  }

  clearStorage(): void {
    localStorage.clear();
  }
}
